import type { Id } from '@/convex/_generated/dataModel';
import {
  isOnboardingImageMimeType,
  ONBOARDING_MAX_FILE_BYTES,
  ONBOARDING_MAX_FILE_COUNT,
  onboardingDataUrlMimeType,
} from '@/lib/uploads/onboarding-media-upload';

export type OnboardingIntent = {
  category: 'design' | 'marketing';
  sourceType: 'upload' | 'preset';
  presetId?: Id<'globalProductPresets'>;
  productPresetSubtypeId?: string;
  stylePresetId?: Id<'globalStylePresets'>;
  colorPresetIds?: Array<Id<'globalColorPresets'> | null>;
  presetName?: string;
  presetPrompt?: string;
  files?: Array<{
    name: string;
    mimeType: string;
    size: number;
    dataUrl: string;
  }>;
  createdAt: number;
};

export type StoredOnboardingPreviewFile = {
  name: string;
  type: string;
  size: number;
  previewDataUrl: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === 'string';
}

function onboardingDataUrlByteLength(dataUrl: string): number | null {
  const commaIndex = dataUrl.indexOf(',');
  if (commaIndex < 0) return null;
  const metadata = dataUrl.slice(0, commaIndex).toLowerCase();
  const payload = dataUrl.slice(commaIndex + 1);
  try {
    if (metadata.includes(';base64')) {
      const compact = payload.replace(/\s/g, '');
      if (!/^[a-z0-9+/]*={0,2}$/i.test(compact)) return null;
      const padding = compact.endsWith('==')
        ? 2
        : compact.endsWith('=')
          ? 1
          : 0;
      return Math.floor((compact.length * 3) / 4) - padding;
    }
    return new TextEncoder().encode(decodeURIComponent(payload)).byteLength;
  } catch {
    return null;
  }
}

function isStoredOnboardingFile(value: unknown): boolean {
  if (!isRecord(value) || !isOnboardingImageMimeType(value.mimeType)) {
    return false;
  }
  const actualSize =
    typeof value.dataUrl === 'string'
      ? onboardingDataUrlByteLength(value.dataUrl)
      : null;
  return (
    typeof value.name === 'string' &&
    typeof value.dataUrl === 'string' &&
    onboardingDataUrlMimeType(value.dataUrl) === value.mimeType.toLowerCase() &&
    typeof value.size === 'number' &&
    Number.isFinite(value.size) &&
    value.size >= 0 &&
    value.size <= ONBOARDING_MAX_FILE_BYTES &&
    actualSize !== null &&
    actualSize <= ONBOARDING_MAX_FILE_BYTES
  );
}

/**
 * Treat browser storage as an untrusted serialization boundary. In particular,
 * category controls whether bytes enter private or public storage, so a cast
 * is never sufficient here.
 */
export function isOnboardingIntent(value: unknown): value is OnboardingIntent {
  if (!isRecord(value)) return false;
  if (value.category !== 'design' && value.category !== 'marketing') {
    return false;
  }
  if (value.sourceType !== 'upload' && value.sourceType !== 'preset') {
    return false;
  }
  if (
    typeof value.createdAt !== 'number' ||
    !Number.isFinite(value.createdAt) ||
    value.createdAt < 0
  ) {
    return false;
  }
  if (
    !isOptionalString(value.presetId) ||
    !isOptionalString(value.productPresetSubtypeId) ||
    !isOptionalString(value.stylePresetId) ||
    !isOptionalString(value.presetName) ||
    !isOptionalString(value.presetPrompt)
  ) {
    return false;
  }
  if (
    value.colorPresetIds !== undefined &&
    (!Array.isArray(value.colorPresetIds) ||
      !value.colorPresetIds.every(
        (presetId) => presetId === null || typeof presetId === 'string'
      ))
  ) {
    return false;
  }
  return (
    value.files === undefined ||
    (Array.isArray(value.files) &&
      value.files.length <= ONBOARDING_MAX_FILE_COUNT &&
      value.files.every(isStoredOnboardingFile))
  );
}

export function parseStoredOnboardingIntent(
  value: unknown
): OnboardingIntent | null {
  if (isOnboardingIntent(value)) return value;
  if (!isRecord(value) || !Array.isArray(value.files)) return null;
  const sanitized = {
    ...value,
    files: value.files
      .filter(isStoredOnboardingFile)
      .slice(0, ONBOARDING_MAX_FILE_COUNT),
  };
  return isOnboardingIntent(sanitized) ? sanitized : null;
}

/** Validate the retired home-page handoff format while old browser tabs exist. */
export function parseStoredOnboardingPreviewFiles(
  value: unknown
): StoredOnboardingPreviewFile[] | null {
  if (!Array.isArray(value) || value.length > ONBOARDING_MAX_FILE_COUNT) {
    return null;
  }
  const files: StoredOnboardingPreviewFile[] = [];
  for (const file of value) {
    if (
      !isRecord(file) ||
      typeof file.name !== 'string' ||
      !isOnboardingImageMimeType(file.type) ||
      typeof file.size !== 'number' ||
      !Number.isFinite(file.size) ||
      file.size < 0 ||
      file.size > ONBOARDING_MAX_FILE_BYTES ||
      typeof file.previewDataUrl !== 'string' ||
      onboardingDataUrlMimeType(file.previewDataUrl) !== file.type.toLowerCase()
    ) {
      return null;
    }
    files.push({
      name: file.name,
      type: file.type,
      size: file.size,
      previewDataUrl: file.previewDataUrl,
    });
  }
  return files;
}
